import React, { useState, useEffect, useRef, useMemo } from "react";
import { Spin, message } from "antd";
import { PlusOutlined, DollarOutlined, CopyOutlined } from "@ant-design/icons";
import { getRentCollectionBillList } from "../../api/rent-collection";
import { fmtBj, fromBj, dueInfo, daysFromToday } from "../../utils/bjDate";
import { copyText } from "../../utils/copyText";
import RentBillDrawer from "./RentBillDrawer";
import SettlementDrawer from "./SettlementDrawer";
import {
  BILL_TYPE_MAP,
  ALL_BILL_STATUS,
  WATER_PRICE,
  ELECTRICITY_PRICE,
} from "./rentBillConstants";

const ACTIVE = ["unpaid", "partiallyPaid"]; // 待付款
// 租金、水电、物业账单必须起止日期齐全才能结算（没有结束日期就无法生成下一期）；押金、其他不要求
const NEED_DATES = ["rent", "waterElectricity", "propertyFee"];
const datesMissing = (b) =>
  NEED_DATES.includes(b.billType) && (!b.startDate || !b.endDate);
const TYPE_ORDER = {
  rent: 1,
  waterElectricity: 2,
  propertyFee: 3,
  deposit: 4,
  other: 5,
};

// 分级条：级别是累计的，后一级包含前一级。level 3(statuses=null) 为全部
const LEVELS = [
  { key: 1, label: "待付款", statuses: ACTIVE },
  { key: 2, label: "含已付", statuses: [...ACTIVE, "paid"] },
  { key: 3, label: "全部", statuses: null },
];
const STATUS_TEXT = {
  unpaid: { label: "未付款", color: "#64748b" },
  partiallyPaid: { label: "部分付款", color: "#d97706" },
  paid: { label: "已付款", color: "#16a34a" },
  closed: { label: "已关闭", color: "#94a3b8" },
  cancelled: { label: "已取消", color: "#94a3b8" },
};
const n2 = (v) =>
  v == null
    ? "-"
    : Number(v).toLocaleString("zh-CN", { maximumFractionDigits: 2 });

// 待付款在前：按账单日升序（逾期最久的在前）；其余按账单日倒序；同日按账单类型
const sortBills = (list) =>
  [...list].sort((a, b) => {
    const aa = ACTIVE.includes(a.status);
    const ba = ACTIVE.includes(b.status);
    if (aa !== ba) return aa ? -1 : 1;
    const da = daysFromToday(a.dueDate) ?? 0;
    const db = daysFromToday(b.dueDate) ?? 0;
    const d = aa ? da - db : db - da;
    return d || (TYPE_ORDER[a.billType] || 9) - (TYPE_ORDER[b.billType] || 9);
  });

function BillCard({ bill, roomNumber, onClick }) {
  const active = ACTIVE.includes(bill.status);
  const dim = bill.status === "closed" || bill.status === "cancelled";

  // 账单日颜色 + 逾期/临期提示（仅待付款的账单）
  const { days, status: ds } = dueInfo(bill.dueDate);
  let hint = null;
  let dateColor = "#475569";
  if (active && days !== null) {
    if (ds === "overdue") {
      hint = `逾期${-days}天`;
      dateColor = "#dc2626";
    } else if (days === 0) {
      hint = "今天到期";
      dateColor = "#d97706";
    } else if (ds === "warning") {
      hint = `${days}天后到期`;
      dateColor = "#d97706";
    }
  }

  // 强调色（左侧色条 + 金额）：逾期红；今天/7天内到期橙；已付绿；关闭/取消灰；其余待付款蓝
  let accent = "#3b82f6";
  if (bill.status === "paid") accent = "#16a34a";
  else if (!active) accent = "#94a3b8";
  else if (ds === "overdue") accent = "#dc2626";
  else if (ds === "warning") accent = "#d97706";

  // 类型标签：other 类型有自定义名称时显示名称（自动生成的"其他-房号-日期"不算）
  const customName =
    bill.billType === "other" &&
    bill.billNames &&
    !bill.billNames.startsWith(`${BILL_TYPE_MAP.other}-`)
      ? bill.billNames
      : null;
  const typeLabel = customName || BILL_TYPE_MAP[bill.billType] || bill.billType;

  const start = fromBj(bill.startDate);
  const end = fromBj(bill.endDate);
  const period = start
    ? end
      ? `${start.format("YYYY/MM/DD")} - ${end.format("YYYY/MM/DD")}（${end.diff(start, "day")}天）`
      : start.format("YYYY/MM/DD")
    : null;

  return (
    <div
      className={`rcd-bill${dim ? " dim" : ""}`}
      style={{ "--c": accent }}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
    >
      <div className="rcd-bill-top">
        <div className="rcd-bill-l">
          <span className="rcd-bill-room">{roomNumber || "-"}</span>
          <span className="rcd-bill-type">{typeLabel}</span>
        </div>
        <div className="rcd-bill-r">
          <div>
            <div className="rcd-bill-date" style={{ color: dateColor }}>
              {fmtBj(bill.dueDate, "YYYY/MM/DD")}
            </div>
            {hint && (
              <div className="rcd-bill-hint" style={{ color: dateColor }}>
                {hint}
              </div>
            )}
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="rcd-bill-amt" style={{ color: accent }}>
              ¥{n2(bill.paidAmount ?? 0)}/{n2(bill.amount)}
            </div>
            <div
              className="rcd-bill-hint"
              style={{ color: STATUS_TEXT[bill.status]?.color }}
            >
              {STATUS_TEXT[bill.status]?.label}
            </div>
          </div>
        </div>
      </div>

      {(period ||
        bill.billType === "waterElectricity" ||
        bill.notes ||
        (ACTIVE.includes(bill.status) && datesMissing(bill))) && (
        <div className="rcd-bill-body">
          {period && <div>周期: {period}</div>}
          {bill.billType === "waterElectricity" && (
            <>
              <div>
                水表:{" "}
                {`${bill.waterMeterStart ?? ""} - ${bill.waterMeterEnd ?? ""}`}
              </div>
              <div>
                电表:{" "}
                {`${bill.electricityMeterStart ?? ""} - ${bill.electricityMeterEnd ?? ""}`}
              </div>
            </>
          )}
          {ACTIVE.includes(bill.status) && datesMissing(bill) && (
            <div style={{ color: "#d97706" }}>
              缺少{!bill.startDate ? "开始" : "结束"}日期，暂不能结算
            </div>
          )}
          {bill.notes ? <div>备注: {bill.notes}</div> : null}
        </div>
      )}
    </div>
  );
}

export default function RentBillList({ contract, checkContract }) {
  const [loading, setLoading] = useState(false);
  const [allBills, setAllBills] = useState([]); // 该合约全部账单（原始数据）
  const [level, setLevel] = useState(1);
  const [billModalOpen, setBillModalOpen] = useState(false);
  const [showBillId, setShowBillId] = useState(null);
  const [showSettleModal, setShowSettleModal] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    if (!contract?._id) return;
    loadBills();
  }, [contract]);

  // 一次拉全部状态，分级条在前端过滤：切换级别不再请求，也没有切换时的竞态
  const loadBills = async () => {
    const s = ++seq.current;
    setLoading(true);
    try {
      const res = await getRentCollectionBillList(contract._id, {
        status: ALL_BILL_STATUS,
      });
      if (s !== seq.current) return; // 过期响应丢弃
      setAllBills(res.data.data || []);
    } catch (e) {
      if (s === seq.current)
        message.error(e?.response?.data?.message || "加载账单失败");
    } finally {
      if (s === seq.current) setLoading(false);
    }
  };

  // 可结算账单与筛选无关，始终取全量里的待付款
  const settleBills = useMemo(
    () =>
      allBills.filter(
        (b) => ACTIVE.includes(b.status) && b.amount && !datesMissing(b),
      ),
    [allBills],
  );
  const blockedCount = useMemo(
    () =>
      allBills.filter(
        (b) => ACTIVE.includes(b.status) && b.amount && datesMissing(b),
      ).length,
    [allBills],
  );
  const settleValid =
    settleBills.length > 0 || contract.status === "pendingSettlement";

  const counts = useMemo(
    () =>
      LEVELS.map((lv) =>
        lv.statuses
          ? allBills.filter((b) => lv.statuses.includes(b.status)).length
          : allBills.length,
      ),
    [allBills],
  );
  const visible = useMemo(() => {
    const lv = LEVELS.find((l) => l.key === level);
    return sortBills(
      lv.statuses
        ? allBills.filter((b) => lv.statuses.includes(b.status))
        : allBills,
    );
  }, [allBills, level]);

  const openBillModal = (id) => {
    setShowBillId(id);
    setBillModalOpen(true);
  };

  // 催款话术：必须在点击的同步调用链里直接复制，中间不要加 await
  const copySettleWords = () => {
    const fmt = (d) => fmtBj(d, "YYYY/MM/DD");
    let words = `老板好，咱们${contract.host.roomNumber} 有费用需要结算\n`;
    let total = 0;
    let totalPaid = 0;
    settleBills.forEach((item) => {
      if (item.billType === "rent") {
        words += `${fmt(item.startDate)} ~ ${fmt(item.endDate)} 房租 ${item.amount} 元\n`;
      } else if (item.billType === "waterElectricity") {
        const w = item.waterMeterEnd - item.waterMeterStart;
        const e = item.electricityMeterEnd - item.electricityMeterStart;
        words += `水：${item.waterMeterEnd}-${item.waterMeterStart}=${w}*${WATER_PRICE}=${w * WATER_PRICE}\n`;
        words += `电：${item.electricityMeterEnd}-${item.electricityMeterStart}=${e}${
          ELECTRICITY_PRICE !== 1
            ? `*${ELECTRICITY_PRICE}=${e * ELECTRICITY_PRICE}`
            : ""
        }\n`;
      } else if (item.billType === "deposit") {
        words += `押金 ¥${item.amount} \n`;
      } else if (item.billType === "propertyFee") {
        words += `${fmt(item.startDate)} ~ ${fmt(item.endDate)} 物业费 ${item.amount} 元\n`;
      } else if (item.billType === "other") {
        words += `其他费用 ¥${item.amount} \n`;
      }
      total += item.amount;
      totalPaid += item?.paidAmount || 0;
    });
    words += `合计：${total}元${
      totalPaid > 0
        ? `，其中 ${totalPaid} 元已付，您需支付 ${total - totalPaid} 元`
        : ""
    } \n`;
    copyText(words)
      .then(() => message.success("催款话术已复制到剪切板"))
      .catch((err) => message.error(`复制失败：${err?.message || err}`));
  };

  return (
    <div>
      {/* 工具条：左 分级条，右 操作（放不下时操作自动换到下一行靠右） */}
      <div className="rcd-toolbar">
        <div className="rcd-lv" role="radiogroup" aria-label="账单范围">
          {LEVELS.map((lv, i) => (
            <button
              key={lv.key}
              type="button"
              role="radio"
              aria-checked={level === lv.key}
              className={level === lv.key ? "on" : level > lv.key ? "in" : ""}
              onClick={() => setLevel(lv.key)}
            >
              {lv.label}
              <span className="n">{counts[i]}</span>
            </button>
          ))}
        </div>
        <div className="rcd-tools">
          <button
            type="button"
            className="rcd-btn sm"
            onClick={() => openBillModal()}
          >
            <PlusOutlined />
            新增账单
          </button>
          <button
            type="button"
            className="rcd-btn sm primary"
            onClick={() => setShowSettleModal(true)}
            disabled={!settleValid}
          >
            <DollarOutlined />
            快速结算
          </button>
          <button
            type="button"
            className="rcd-btn sm"
            onClick={copySettleWords}
            disabled={!settleValid}
          >
            <CopyOutlined />
            催款话术
          </button>
        </div>
      </div>
      {blockedCount > 0 && (
        <div className="rcd-warn">
          有 {blockedCount}{" "}
          张账单缺少开始或结束日期，补全后才能结算（点开账单补充）
        </div>
      )}
      <RentBillDrawer
        open={billModalOpen}
        id={showBillId}
        adviceContract={contract}
        billChange={loadBills}
        close={() => setBillModalOpen(false)}
      />
      <SettlementDrawer
        open={showSettleModal}
        contract={contract}
        bills={settleBills}
        billChange={checkContract}
        onCancel={() => setShowSettleModal(false)}
      />

      <div
        className="rcd-bill-grid"
        style={{ opacity: loading && allBills.length ? 0.6 : 1 }}
      >
        {visible.map((item) => (
          <BillCard
            key={item._id}
            bill={item}
            roomNumber={item.host?.roomNumber || contract.host?.roomNumber}
            onClick={() => openBillModal(item._id)}
          />
        ))}
      </div>

      {!visible.length &&
        (loading ? (
          <div className="rcd-empty">
            <Spin />
          </div>
        ) : (
          <div className="rcd-empty">
            {level === 1 && allBills.length > 0 ? (
              <>
                没有待付款账单
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="rcd-btn sm"
                    onClick={() => setLevel(2)}
                  >
                    查看已付账单
                  </button>
                </div>
              </>
            ) : (
              "暂无账单"
            )}
          </div>
        ))}
    </div>
  );
}
